import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MeetingParticipant, ChatMessage, SessionState } from './meeting/session';
import { BOARD_PALETTE, type Board, type BoardPoint, type BoardStroke } from './meeting/board';
import { useI18n, type Translate } from './i18n';
import type { MeetingRole } from './scenarios/scenes';

export function Avatar({ name, small = false }: { name: string; small?: boolean }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <div className={small ? 'nm-avatar-sm' : 'nm-avatar'} aria-hidden="true">
      {initials || '?'}
    </div>
  );
}

const QUALITY_KEYS = [
  'meeting.badge.quality.unknown',
  'meeting.badge.quality.excellent',
  'meeting.badge.quality.good',
  'meeting.badge.quality.fair',
  'meeting.badge.quality.poor',
  'meeting.badge.quality.bad',
  'meeting.badge.quality.down',
] as const;

export function messageText(message: ChatMessage, t: Translate): string {
  if (message.textKey) return t(message.textKey, message.params);
  return message.text;
}

/** Renders one participant's video (or avatar fallback) plus status badges. */
export function VideoTile({
  participant,
  stream,
  spotlight = false,
  background,
  className,
}: {
  participant: MeetingParticipant;
  stream?: MediaStream | null;
  spotlight?: boolean;
  background?: string | null;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { t } = useI18n();
  // FakeTransport (offline demo) returns a structural `{getTracks()}` object rather than a real
  // MediaStream; browsers reject that in `srcObject`, so normalize it into a MediaStream.
  const mediaStream = useMemo<MediaStream | null>(() => {
    if (!stream) return null;
    if (stream instanceof MediaStream) return stream;
    const tracks =
      (stream as unknown as { getTracks?: () => MediaStreamTrack[] }).getTracks?.() ?? [];
    return tracks.length > 0 ? new MediaStream(tracks) : null;
  }, [stream]);
  // Attach through a callback ref, not an effect keyed only on the stream: when a participant turns
  // the camera off the <video> element is unmounted, and on unmute React creates a *new* element while
  // `mediaStream` keeps the same identity - a [mediaStream] effect would not run again and the tile
  // would stay black even though frames arrive. The callback ref runs on every element mount/change.
  const attachVideo = useCallback(
    (element: HTMLVideoElement | null) => {
      videoRef.current = element;
      if (!element) return;
      if (element.srcObject !== mediaStream) element.srcObject = mediaStream;
      if (mediaStream) void element.play().catch(() => undefined);
    },
    [mediaStream],
  );
  // Keep the element in sync when the underlying track is swapped without remounting the tile.
  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;
    if (element.srcObject !== mediaStream) element.srcObject = mediaStream;
    if (mediaStream) void element.play().catch(() => undefined);
  }, [mediaStream]);

  const showingVideo = Boolean(mediaStream) && !participant.videoMuted;
  const filter = participant.isLocal
    ? background === 'blur'
      ? 'blur(6px)'
      : background && background !== 'none'
        ? 'brightness(0.9)'
        : 'none'
    : 'none';
  const quality = participant.networkQuality;

  return (
    <div
      className={`nm-tile${participant.speaking ? ' is-speaking' : ''}${participant.role === 'host' ? ' is-hosting' : ''}${className ? ` ${className}` : ''}`}
      style={spotlight ? { maxHeight: '100%' } : undefined}
      data-testid={`tile-${participant.uid}`}
      data-local={participant.isLocal ? 'true' : 'false'}
    >
      {showingVideo ? (
        <video
          ref={attachVideo}
          autoPlay
          playsInline
          muted={participant.isLocal}
          style={{ transform: 'scaleX(-1)', filter }}
        />
      ) : (
        <>
          <Avatar name={participant.displayName} />
          {participant.isLocal && participant.videoMuted ? (
            <div className="nm-tile-hint">{t('join.preview.off')}</div>
          ) : null}
        </>
      )}
      <div className="nm-tile-badges">
        {participant.handRaised ? (
          <span className="nm-badge is-hand" title={t('meeting.control.hand')}>
            ✋
          </span>
        ) : null}
        {participant.audioMuted ? (
          <span className="nm-badge is-muted">{t('meeting.badge.micOff')}</span>
        ) : null}
        {quality !== null && quality > 0 ? (
          <span className="nm-badge">
            {t(QUALITY_KEYS[Math.min(6, Math.max(0, quality))] ?? QUALITY_KEYS[0])}
          </span>
        ) : null}
        {participant.role === 'host' ? (
          <span className="nm-badge is-host">{t('meeting.badge.host')}</span>
        ) : null}
      </div>
      <div className="nm-tile-name">
        <span>
          {participant.displayName}
          {participant.isLocal ? ` (${t('common.you')})` : ''}
        </span>
      </div>
    </div>
  );
}

/**
 * Hidden sink for a remote microphone track. The LiveKit SDK subscribes audio but never auto-plays
 * it; without this element remote participants are visible but silent.
 */
export function RemoteAudio({ stream }: { stream: MediaStream | null }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const trackId = stream?.getAudioTracks()[0]?.id ?? '';
  useEffect(() => {
    const element = audioRef.current;
    if (!element) return;
    element.srcObject = stream;
    if (stream) void element.play().catch(() => undefined);
    return () => {
      if (element) element.srcObject = null;
    };
  }, [stream, trackId]);
  return <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />;
}

/** Conference control bar (kept for the `meeting` scene). */
export function ControlBar({
  state,
  onToggleMic,
  onToggleCamera,
  onToggleShare,
  onToggleChat,
  onToggleParticipants,
  onToggleBoard,
  onOpenSettings,
  onOpenInvite,
  onOpenManage,
  onRaiseHand,
  onLeave,
  onRenew,
}: {
  state: SessionState;
  onToggleMic: () => void;
  onToggleCamera: () => void;
  onToggleShare: () => void;
  onToggleChat: () => void;
  onToggleParticipants: () => void;
  onToggleBoard: () => void;
  onOpenSettings: () => void;
  onOpenInvite: () => void;
  onOpenManage: () => void;
  onRaiseHand: () => void;
  onLeave: () => void;
  onRenew: () => void;
}) {
  const local = state.participants.find((entry) => entry.isLocal);
  const isHost = local?.role === 'host';
  const { t } = useI18n();
  return (
    <div
      className="nm-controls"
      role="toolbar"
      aria-label={t('room.controls')}
      data-testid="room-controls"
    >
      <button
        className="nm-btn"
        aria-pressed={state.micEnabled}
        onClick={onToggleMic}
        title={state.micEnabled ? t('meeting.control.micOn') : t('meeting.control.micOff')}
        aria-label={state.micEnabled ? t('meeting.control.micOn') : t('meeting.control.micOff')}
        data-testid="room-mic"
      >
        {state.micEnabled ? '🎙' : '🔇'}
      </button>
      <button
        className="nm-btn"
        aria-pressed={state.cameraEnabled}
        onClick={onToggleCamera}
        title={state.cameraEnabled ? t('meeting.control.cameraOn') : t('meeting.control.cameraOff')}
        aria-label={
          state.cameraEnabled ? t('meeting.control.cameraOn') : t('meeting.control.cameraOff')
        }
        data-testid="room-camera"
      >
        {state.cameraEnabled ? '🎥' : '🚫'}
      </button>
      <button
        className="nm-btn"
        aria-pressed={state.screenSharing}
        onClick={onToggleShare}
        title={t('meeting.control.share')}
        data-testid="room-share"
      >
        🖥
      </button>
      <button
        className={`nm-btn${state.chatOpen ? ' is-active' : ''}`}
        onClick={onToggleChat}
        title={t('meeting.control.chat')}
        data-testid="room-chat"
      >
        💬{state.unread > 0 ? ` ${state.unread}` : ''}
      </button>
      <button
        className={`nm-btn${state.boardOpen ? ' is-active' : ''}`}
        onClick={onToggleBoard}
        title={t('meeting.control.board')}
        data-testid="room-board"
      >
        ✏️
      </button>
      <button
        className="nm-btn"
        onClick={onToggleParticipants}
        title={t('meeting.control.people')}
        data-testid="room-participants"
      >
        👥 {state.participants.length}
      </button>
      <button
        className="nm-btn"
        aria-pressed={local?.handRaised ?? false}
        onClick={onRaiseHand}
        title={t('meeting.control.hand')}
        data-testid="room-hand"
      >
        ✋
      </button>
      <button
        className="nm-btn"
        onClick={onOpenInvite}
        title={t('room.invite')}
        data-testid="room-invite"
      >
        ➕
      </button>
      <button className="nm-btn" onClick={onRenew} title={t('room.renew')} data-testid="room-renew">
        ⟳
      </button>
      {isHost ? (
        <button
          className="nm-btn"
          onClick={onOpenManage}
          title={t('meeting.control.manage')}
          data-testid="room-manage"
        >
          🛡
        </button>
      ) : null}
      <button
        className="nm-btn"
        onClick={onOpenSettings}
        title={t('room.settings')}
        aria-label={t('room.settings')}
        data-testid="room-settings"
      >
        ⚙️
      </button>
      <button className="nm-btn is-danger nm-leave" onClick={onLeave} data-testid="room-leave-big">
        {t('room.leave')}
      </button>
    </div>
  );
}

export function ParticipantsPanel({
  state,
  onMuteAll,
  onKick,
  onRole,
  canModerate,
}: {
  state: SessionState;
  localUid: number | null;
  onMuteAll: () => void;
  onKick: (uid: number) => void;
  onRole: (uid: number, role: MeetingRole) => void;
  canModerate: boolean;
}) {
  const { t } = useI18n();
  return (
    <div className="nm-card" data-testid="participants-panel">
      <h2>{t('meeting.participants.title', { count: state.participants.length })}</h2>
      {canModerate ? (
        <div className="nm-row" style={{ marginBottom: 8 }}>
          <button className="nm-btn" onClick={onMuteAll} data-testid="participants-mute-all">
            {t('meeting.participants.muteAll')}
          </button>
        </div>
      ) : null}
      <div className="nm-scroll">
        {state.participants.map((participant) => (
          <div
            className="nm-person"
            key={participant.uid}
            data-testid={`participant-row-${participant.uid}`}
          >
            <Avatar name={participant.displayName} small />
            <div className="nm-person-main">
              <div className="nm-person-name">
                {participant.displayName}
                {participant.isLocal ? ` (${t('common.you')})` : ''}
              </div>
              <div className="nm-person-meta">
                {participant.role === 'host' ? t('meeting.badge.host') : t('join.role.audience')}
                {participant.audioMuted ? ` · ${t('meeting.participants.muted')}` : ''}
                {participant.handRaised ? ` · ${t('meeting.participants.handUp')}` : ''}
              </div>
            </div>
            {canModerate && !participant.isLocal ? (
              <div className="nm-person-actions">
                <button
                  className="nm-btn"
                  onClick={() => onKick(participant.uid)}
                  title={t('meeting.participants.remove')}
                  data-testid={`participant-kick-${participant.uid}`}
                >
                  ✕
                </button>
                <button
                  className="nm-btn"
                  onClick={() =>
                    onRole(participant.uid, participant.role === 'host' ? 'audience' : 'host')
                  }
                  title={
                    participant.role === 'host'
                      ? t('meeting.participants.makeAudience')
                      : t('meeting.participants.makeHost')
                  }
                  data-testid={`participant-role-${participant.uid}`}
                >
                  {participant.role === 'host' ? '↓' : '↑'}
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ChatPanel({
  state,
  onSend,
  onClose,
  onClearHistory,
  title,
  placeholder,
  testId = 'chat-panel',
}: {
  state: SessionState;
  onSend: (text: string) => void;
  onClose?: () => void;
  /** Clears this browser's local history for the current channel (restored messages). */
  onClearHistory?: () => void;
  title?: string;
  placeholder?: string;
  testId?: string;
}) {
  const [draft, setDraft] = useState('');
  const endRef = useRef<HTMLDivElement | null>(null);
  const { t } = useI18n();
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [state.messages.length]);
  const submit = useCallback(() => {
    if (draft.trim().length === 0) return;
    onSend(draft);
    setDraft('');
  }, [draft, onSend]);
  return (
    <div className="nm-card" data-testid={testId}>
      <div className="nm-row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>{title ?? t('meeting.chat.title')}</h2>
        {onClose ? (
          <button
            className="nm-btn is-ghost"
            onClick={onClose}
            aria-label={t('meeting.chat.close')}
          >
            ✕
          </button>
        ) : null}
      </div>
      {state.historyRestored > 0 ? (
        <div className="nm-row" data-testid={`${testId}-history-note`} style={{ marginBottom: 8 }}>
          <span className="nm-hint">
            {t('meeting.chat.historyRestored', { count: state.historyRestored })}
          </span>
          {onClearHistory ? (
            <button
              className="nm-btn is-ghost"
              onClick={onClearHistory}
              data-testid={`${testId}-clear-history`}
            >
              {t('meeting.chat.clearHistory')}
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="nm-chat">
        <div
          className="nm-messages"
          role="log"
          aria-live="polite"
          data-testid={`${testId}-messages`}
        >
          {state.messages.map((message: ChatMessage) => (
            <div
              key={message.id}
              className={`nm-msg${message.kind === 'system' ? ' is-system' : ''}`}
              data-testid={`chat-message-${message.id}`}
            >
              <div className="nm-msg-head">
                <span>{message.kind === 'system' ? t('common.system') : message.fromName}</span>
                <span>{new Date(message.at).toLocaleTimeString()}</span>
              </div>
              <div className="nm-msg-text">{messageText(message, t)}</div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="nm-composer">
          <input
            className="nm-input"
            value={draft}
            placeholder={placeholder ?? t('meeting.chat.placeholder')}
            aria-label={t('meeting.chat.label')}
            data-testid={`${testId}-input`}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
          />
          <button
            className="nm-btn is-active"
            onClick={submit}
            disabled={draft.trim().length === 0}
            data-testid={`${testId}-send`}
          >
            {t('common.send')}
          </button>
        </div>
      </div>
    </div>
  );
}

export function BoardPanel({
  board,
  readOnly,
  onStroke,
  onUndo,
  onClear,
  onClose,
  color,
  onColor,
  width,
  onWidth,
  eraser,
  onEraser,
}: {
  board: Board;
  readOnly: boolean;
  onStroke: (stroke: BoardStroke) => void;
  onUndo: () => void;
  onClear: () => void;
  onClose: () => void;
  color: string;
  onColor: (color: string) => void;
  width: number;
  onWidth: (width: number) => void;
  eraser: boolean;
  onEraser: (eraser: boolean) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const currentRef = useRef<BoardStroke | null>(null);
  const [, forceRender] = useState(0);
  const { t } = useI18n();

  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    if (
      canvas.width !== Math.round(rect.width * dpr) ||
      canvas.height !== Math.round(rect.height * dpr)
    ) {
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
    }
    const context = canvas.getContext('2d');
    if (!context) return;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.clearRect(0, 0, rect.width, rect.height);
    const strokes = currentRef.current ? [...board, currentRef.current] : board;
    for (const stroke of strokes) {
      if (stroke.points.length === 0) continue;
      context.beginPath();
      context.strokeStyle = stroke.eraser ? '#0a0f1a' : stroke.color;
      context.lineWidth = stroke.width;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      stroke.points.forEach((point, index) => {
        const x = point.x * rect.width;
        const y = point.y * rect.height;
        if (index === 0) context.moveTo(x, y);
        else context.lineTo(x, y);
      });
      context.stroke();
    }
  }, [board]);

  useEffect(() => {
    paint();
  }, [paint]);

  useEffect(() => {
    const onResize = () => paint();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [paint]);

  const point = (event: React.PointerEvent<HTMLCanvasElement>): BoardPoint => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    };
  };

  return (
    <div className="nm-board" data-testid="board-panel">
      <div className="nm-row" style={{ justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0 }}>{t('meeting.board.title')}</h2>
        <button className="nm-btn is-ghost" onClick={onClose} aria-label={t('meeting.board.close')}>
          ✕
        </button>
      </div>
      <canvas
        ref={canvasRef}
        data-testid="board-canvas"
        role="img"
        aria-label={t('meeting.board.label')}
        onPointerDown={(event) => {
          if (readOnly) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          currentRef.current = {
            id: `st_${Math.random().toString(36).slice(2, 10)}`,
            color,
            width,
            eraser,
            author: 0,
            points: [point(event)],
          };
          forceRender((value) => value + 1);
          paint();
        }}
        onPointerMove={(event) => {
          if (readOnly || !currentRef.current) return;
          const next = point(event);
          const current = currentRef.current;
          currentRef.current = { ...current, points: [...current.points, next] };
          forceRender((value) => value + 1);
          paint();
        }}
        onPointerUp={() => {
          if (readOnly || !currentRef.current) return;
          const stroke = currentRef.current;
          currentRef.current = null;
          if (stroke.points.length > 0) onStroke(stroke);
          paint();
        }}
        onPointerCancel={() => {
          currentRef.current = null;
          paint();
        }}
      />
      {readOnly ? <div className="nm-sub">{t('meeting.board.readOnly')}</div> : null}
      <div className="nm-board-tools">
        {BOARD_PALETTE.map((swatch) => (
          <button
            key={swatch}
            className="nm-swatch"
            style={{ background: swatch }}
            aria-label={t('meeting.board.swatch', { color: swatch })}
            aria-pressed={color === swatch}
            onClick={() => {
              onColor(swatch);
              onEraser(false);
            }}
          />
        ))}
        <button className="nm-btn" aria-pressed={eraser} onClick={() => onEraser(!eraser)}>
          {t('meeting.board.eraser')}
        </button>
        <label className="nm-row" style={{ gap: 6 }}>
          <span className="nm-sub">{t('meeting.board.width')}</span>
          <input
            type="range"
            min={1}
            max={16}
            value={width}
            onChange={(event) => onWidth(Number(event.target.value))}
          />
        </label>
        <button className="nm-btn" onClick={onUndo} disabled={readOnly} data-testid="board-undo">
          {t('meeting.board.undo')}
        </button>
        <button className="nm-btn" onClick={onClear} disabled={readOnly} data-testid="board-clear">
          {t('meeting.board.clear')}
        </button>
      </div>
    </div>
  );
}

export function Modal({
  title,
  children,
  onClose,
  actions,
  testId,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  actions?: React.ReactNode;
  testId?: string;
}) {
  const { t } = useI18n();
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="nm-modal-backdrop"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="nm-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
      >
        <h3>{title}</h3>
        {children}
        <div className="nm-modal-actions">
          {actions ?? (
            <button className="nm-btn" onClick={onClose}>
              {t('common.close')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function Banner({
  tone = 'info',
  children,
  action,
}: {
  tone?: 'info' | 'warn' | 'error';
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className={`nm-banner${tone === 'info' ? '' : ` is-${tone}`}`} role="status">
      <span>{children}</span>
      {action}
    </div>
  );
}

export function useToast(): { toast: string | null; show: (message: string) => void } {
  const [toast, setToast] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const show = useCallback((message: string) => {
    setToast(message);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToast(null), 2800);
  }, []);
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );
  return { toast, show };
}

export function ToastHost({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="nm-toast" role="status" data-testid="toast">
      {message}
    </div>
  );
}

export function useSessionState<T>(
  subscribe: (listener: () => void) => () => void,
  read: () => T,
): T {
  const [value, setValue] = useState<T>(read);
  useEffect(() => {
    const unsubscribe = subscribe(() => setValue(read()));
    setValue(read());
    return unsubscribe;
  }, [subscribe, read]);
  return value;
}

export const memoized = { useMemo };
